import { Link } from 'react-router-dom';
import { ArrowLeft, Store, Camera, CheckCircle, Clock, DollarSign, Image, MapPin, Star, Users, Zap } from 'lucide-react';

export default function HowToSell() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/" className="flex items-center gap-2 text-primary mb-6 hover:underline">
          <ArrowLeft className="w-5 h-5" />
          Volver al inicio
        </Link>

        <h1 className="text-3xl font-black mb-2">
          <span className="text-primary">Cómo Vender</span> en MotoMarket CR
        </h1>
        <p className="text-gray-600 mb-8">Guía completa para vender repuestos y servicios en nuestra plataforma</p>

        <div className="space-y-8">
          {/* Paso 1 */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-bold text-xl">1</span>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                  <Store className="w-6 h-6 text-primary" />
                  Crea tu cuenta
                </h2>
                <p className="text-gray-600 mb-4">
                  Regístrate en MotoMarket CR proporcionando tu información básica. Elige el plan que mejor se adapte a tus necesidades.
                </p>
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-sm text-blue-700">
                    <strong>Importante:</strong> Las cuentas gratuitas se activan automáticamente. Los planes PRO y PREMIUM requieren aprobación del administrador.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Paso 2 */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-bold text-xl">2</span>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                  <Image className="w-6 h-6 text-primary" />
                  Publica tus productos
                </h2>
                <p className="text-gray-600 mb-4">
                  Sube fotos claras de tus repuestos, agrega una descripción detallada, precio y la información de compatibilidad.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Usa fotos de buena calidad desde múltiples ángulos
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Incluye el modelo y año de la moto compatible
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Describe el estado (nuevo, usado, original, genérico)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Indica si el precio es negociable
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Paso 3 */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-bold text-xl">3</span>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                  <MapPin className="w-6 h-6 text-primary" />
                  Configura tu ubicación
                </h2>
                <p className="text-gray-600 mb-4">
                  Si tienes un taller o repuestera, configúralo en el mapa para que los compradores puedan encontrarte fácilmente.
                </p>
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-sm text-amber-700">
                    <strong>Nota:</strong> La ubicación en el mapa está disponible solo para planes PRO y PREMIUM.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Paso 4 */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-bold text-xl">4</span>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                  <Users className="w-6 h-6 text-primary" />
                  Recibe mensajes y realiza ventas
                </h2>
                <p className="text-gray-600 mb-4">
                  Los compradores interesados podrán contactarte directamente a través de la plataforma. Responde rápidamente para cerrar ventas.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Mantén tu información de contacto actualizada
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Responde consultas en menos de 24 horas
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Ofrece información clara sobre envío o entrega
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-gradient-to-r from-primary/10 to-orange-500/10 rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Zap className="w-6 h-6 text-primary" />
              Consejos para vender más
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Camera className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-gray-700 text-sm">Las fotos de calidad aumentan las probabilidades de venta hasta un 70%</p>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-gray-700 text-sm">Actualiza tus productos regularmente para mantenerlos visibles</p>
              </div>
              <div className="flex items-start gap-3">
                <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-gray-700 text-sm">Pide a tus clientes que dejen reseñas para construir tu reputación</p>
              </div>
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-gray-700 text-sm">Precios competitivos atraen más compradores</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-primary rounded-2xl shadow-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">¿Listo para empezar?</h2>
            <p className="text-white/80 mb-6">Crea tu cuenta hoy y comienza a vender en MotoMarket CR</p>
            <Link to="/register" className="inline-block bg-white text-primary font-bold px-8 py-3 rounded-xl hover:bg-gray-100 transition">
              Crear Cuenta
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
